import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Select, MenuItem, FormControl } from '@mui/material';
import { SpreadsheetViewer } from './components/SpreadsheetViewer';
import { VideoManager } from './components/VideoManager';
import { ImportManager } from './components/ImportManager';
import { UploadVideo } from './components/UploadVideo';
import { VOManager } from './components/VOManager';
import { VOEditor } from './components/VOEditor';
import { IntroOutroPage } from './components/IntroOutroPage';
import { OutputManager } from './components/OutputManager';
import { ChartSettings } from './components/ChartSettings';
import { AdminDashboard } from './components/AdminDashboard';
import { PrintReport } from './components/AdminDashboard/PrintReport';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { parse, isValid, format } from 'date-fns';
import { SnackbarProvider } from 'notistack';
import { GoogleSheetsProvider, useGoogleSheets } from './context/GoogleSheetsContext';
import { AuthGate } from './components/AuthGate';
import OAuthConsent from './components/OAuthConsent';
import { UserMenu } from './components/UserMenu';
import './App.css';

function AppHeader({ chartDate }: { chartDate: string }) {
  const { chartCategory, availableChartCategories, setChartCategory } = useGoogleSheets();

  return (
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
        <UserMenu />
      </Box>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        align="center"
        sx={{
          fontFamily: "'Open Sans', sans-serif",
          fontWeight: 600,
          mb: 0,
        }}
      >
        Billboard{' '}
        {availableChartCategories.length > 1 ? (
          <FormControl variant="standard" sx={{ minWidth: 140, verticalAlign: 'baseline' }}>
            <Select
              value={chartCategory}
              onChange={(e) => setChartCategory(e.target.value as string)}
              sx={{
                fontFamily: "'Open Sans', sans-serif",
                fontWeight: 600,
                fontSize: 'inherit',
                '& .MuiSelect-select': { py: 0 },
              }}
              disableUnderline
            >
              {availableChartCategories.map((cat) => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          chartCategory
        )}
        {' '}Chart Reveal {chartDate ? `- ${chartDate}` : ''}
      </Typography>
    </Box>
  );
}

// Helper to get chart-category-namespaced spreadsheet ID from localStorage
function getCurrentSpreadsheetId(chartCategory: string): string {
  // Try category-specific key first, fall back to legacy key
  return localStorage.getItem(`billboard_currentSpreadsheetId_${chartCategory}`)
    || localStorage.getItem('currentSpreadsheetId')
    || '';
}

function AppContent() {
  const { chartCategory } = useGoogleSheets();

  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    const savedDate = localStorage.getItem('lastViewedDate');
    if (savedDate) {
      const parsedDate = parse(savedDate, 'M-d-yy', new Date());
      return isValid(parsedDate) ? parsedDate : null;
    }
    return null;
  });

  const [chartDate, setChartDate] = useState<string>(() => {
    return localStorage.getItem('currentChartDate') || '';
  });

  useEffect(() => {
    if (selectedDate) {
      localStorage.setItem('lastViewedDate', format(selectedDate, 'M-d-yy'));
    }
  }, [selectedDate]);

  useEffect(() => {
    if (chartDate) {
      localStorage.setItem('currentChartDate', chartDate);
    }
  }, [chartDate]);

  const currentSpreadsheetId = getCurrentSpreadsheetId(chartCategory);

  // The admin dashboard (and its standalone client-facing print report) and the
  // MCP OAuth consent screen render without the Billboard app header.
  const location = useLocation();
  const isDashboardRoute = location.pathname.startsWith('/dashboard') || location.pathname.startsWith('/oauth');

  return (
    <Container maxWidth={false} sx={{ fontFamily: "'Open Sans', sans-serif", px: 2 }}>
      <Box sx={{ my: 4 }}>
        {!isDashboardRoute && <AppHeader chartDate={chartDate} />}
        <Routes>
          <Route
            path="/"
            element={
              <SpreadsheetViewer
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
                setChartDate={setChartDate}
              />
            }
          />
          <Route path="/video-manager" element={<VideoManager />} />
          <Route path="/import-manager" element={<ImportManager />} />
          <Route path="/upload-video" element={<UploadVideo />} />
          <Route path="/vo-manager" element={<VOManager spreadsheetId={currentSpreadsheetId || undefined} />} />
          <Route path="/vo-editor" element={<VOEditor voLines={[]} />} />
          <Route path="/intro-outro" element={<IntroOutroPage setChartDate={setChartDate} />} />
          <Route path="/intro-outro/:renderSheetId" element={<IntroOutroPage setChartDate={setChartDate} />} />
          <Route path="/output-manager" element={<OutputManager spreadsheetId={currentSpreadsheetId} />} />
          <Route path="/chart-settings" element={<ChartSettings />} />
          <Route path="/dashboard" element={<AdminDashboard />} />
          <Route path="/dashboard/print" element={<PrintReport />} />
          <Route path="/oauth/consent" element={<OAuthConsent />} />
        </Routes>
      </Box>
    </Container>
  );
}

function App() {
  return (
    <Router>
      <AuthGate>
        <GoogleSheetsProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <SnackbarProvider maxSnack={3}>
              <AppContent />
            </SnackbarProvider>
          </LocalizationProvider>
        </GoogleSheetsProvider>
      </AuthGate>
    </Router>
  );
}

export default App;
